import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";
import type { ExecutionEventCategory, ExecutionEventKind } from "@/data/execution/eventStream";
import type { EscalationKind, EscalationSeverity } from "@/data/observability/escalationSignalsSelector";
import type { SignalClusterGroup, SignalClusterKind } from "@/data/execution/signalClusters";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export type ExecutionLifecycle = "all" | "in_progress" | "blocked" | "ready";
export type ExecutionEventFilter = "all" | ExecutionEventCategory;

export interface ExecutionInstallRow {
  jobId: string;
  jobLabel: string;
  customerName: string;
  city: string;
  installerName: string;
  currentStage: string;
  scheduledInstallDate: string;
  executionAvailable: boolean;
  unavailableReason?: string;
  activePhaseName?: string;
  activePhaseStatus?: string;
  phases: unknown[];
  totalPhotosUploaded: number;
  totalPhotosRequired: number;
  totalChecklistChecked: number;
  totalChecklistTotal: number;
  lastActivityAt?: string;
  awaitingQa: boolean;
  reconciliationPending: boolean;
  isStale: boolean;
  qaFailuresTotal: number;
  lifecycle: Exclude<ExecutionLifecycle, "all">;
  lifecycleSort: number;
  blockerReasons: string[];
  issueCount: number;
  ticketCount: number;
  fieldFeedbackCount: number;
  substitutionCount: number;
  sortAt: string;
}

export interface ExecutionEscalationSignal {
  id: string;
  kind: EscalationKind;
  groupKey: string;
  headline: string;
  detail: string;
  severity: EscalationSeverity;
  jobIds: string[];
  jobCount: number;
  sortRank: number;
}

export interface ExecutionInstallSummary {
  activeCount: number;
  inProgressCount: number;
  blockedCount: number;
  readyCount: number;
  awaitingQaCount: number;
  staleCount: number;
  qaFailureCount: number;
  escalationCount: number;
  highEscalationCount: number;
  mediumEscalationCount: number;
  lowEscalationCount: number;
}

export interface ExecutionEventRow {
  id: string;
  jobId: string;
  jobLabel: string;
  kind: ExecutionEventKind;
  category: ExecutionEventCategory;
  headline: string;
  detail?: string;
  actor?: string;
  at: string;
  sortAt: string;
}

export interface ExecutionSignalClusterRow {
  id: string;
  group: SignalClusterGroup;
  kind: SignalClusterKind;
  label: string;
  count: number;
  detail?: string;
  jobId?: string;
  sortOrder: number;
}

export interface ExecutionInstallPageQuery {
  page: number;
  pageSize: number;
  search?: string;
  lifecycle?: ExecutionLifecycle;
  refreshToken?: number;
}

export interface ExecutionEscalationPageQuery {
  page: number;
  pageSize: number;
  refreshToken?: number;
}

export interface ExecutionEventsPageQuery {
  page: number;
  pageSize: number;
  filter?: ExecutionEventFilter;
  refreshToken?: number;
}

type ExecutionInstallViewRow = {
  job_id?: string | null;
  job_label?: string | null;
  customer_name?: string | null;
  city?: string | null;
  installer_name?: string | null;
  current_stage?: string | null;
  scheduled_install_date?: string | null;
  execution_available?: boolean | null;
  unavailable_reason?: string | null;
  active_phase_name?: string | null;
  active_phase_status?: string | null;
  phases?: unknown[] | null;
  total_photos_uploaded?: number | string | null;
  total_photos_required?: number | string | null;
  total_checklist_checked?: number | string | null;
  total_checklist_total?: number | string | null;
  last_activity_at?: string | null;
  awaiting_qa?: boolean | null;
  reconciliation_pending?: boolean | null;
  is_stale?: boolean | null;
  qa_failures_total?: number | string | null;
  lifecycle?: Exclude<ExecutionLifecycle, "all"> | null;
  lifecycle_sort?: number | string | null;
  blocker_reasons?: string[] | null;
  issue_count?: number | string | null;
  ticket_count?: number | string | null;
  field_feedback_count?: number | string | null;
  substitution_count?: number | string | null;
  sort_at?: string | null;
};

type ExecutionEscalationViewRow = {
  id?: string | null;
  kind?: EscalationKind | null;
  group_key?: string | null;
  headline?: string | null;
  detail?: string | null;
  severity?: EscalationSeverity | null;
  job_ids?: string[] | null;
  job_count?: number | string | null;
  sort_rank?: number | string | null;
};

type ExecutionSummaryViewRow = {
  active_count?: number | string | null;
  in_progress_count?: number | string | null;
  blocked_count?: number | string | null;
  ready_count?: number | string | null;
  awaiting_qa_count?: number | string | null;
  stale_count?: number | string | null;
  qa_failure_count?: number | string | null;
  escalation_count?: number | string | null;
  high_escalation_count?: number | string | null;
  medium_escalation_count?: number | string | null;
  low_escalation_count?: number | string | null;
};

type ExecutionEventViewRow = {
  id?: string | null;
  job_id?: string | null;
  job_label?: string | null;
  kind?: ExecutionEventKind | null;
  category?: ExecutionEventCategory | null;
  headline?: string | null;
  detail?: string | null;
  actor?: string | null;
  at?: string | null;
  sort_at?: string | null;
};

type ExecutionClusterViewRow = {
  id?: string | null;
  cluster_group?: SignalClusterGroup | null;
  kind?: SignalClusterKind | null;
  label?: string | null;
  count?: number | string | null;
  detail?: string | null;
  job_id?: string | null;
  sort_order?: number | string | null;
};

const INSTALL_COLUMNS = [
  "job_id",
  "job_label",
  "customer_name",
  "city",
  "installer_name",
  "current_stage",
  "scheduled_install_date",
  "execution_available",
  "unavailable_reason",
  "active_phase_name",
  "active_phase_status",
  "phases",
  "total_photos_uploaded",
  "total_photos_required",
  "total_checklist_checked",
  "total_checklist_total",
  "last_activity_at",
  "awaiting_qa",
  "reconciliation_pending",
  "is_stale",
  "qa_failures_total",
  "lifecycle",
  "lifecycle_sort",
  "blocker_reasons",
  "issue_count",
  "ticket_count",
  "field_feedback_count",
  "substitution_count",
  "sort_at",
].join(", ");

const ESCALATION_COLUMNS = [
  "id",
  "kind",
  "group_key",
  "headline",
  "detail",
  "severity",
  "job_ids",
  "job_count",
  "sort_rank",
].join(", ");

const SUMMARY_COLUMNS = [
  "active_count",
  "in_progress_count",
  "blocked_count",
  "ready_count",
  "awaiting_qa_count",
  "stale_count",
  "qa_failure_count",
  "escalation_count",
  "high_escalation_count",
  "medium_escalation_count",
  "low_escalation_count",
].join(", ");

const EVENT_COLUMNS = [
  "id",
  "job_id",
  "job_label",
  "kind",
  "category",
  "headline",
  "detail",
  "actor",
  "at",
  "sort_at",
].join(", ");

const CLUSTER_COLUMNS = [
  "id",
  "cluster_group",
  "kind",
  "label",
  "count",
  "detail",
  "job_id",
  "sort_order",
].join(", ");

function num(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function int(value: unknown): number {
  return Math.max(0, Math.trunc(num(value)));
}

function sanitizedSearchPattern(search: string): string {
  return `%${search.trim().replace(/[%_]/g, "").replace(/,/g, " ")}%`;
}

async function withPageMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  const startedAt = observeQueryStart(key);
  try {
    const result = await run();
    recordQueryMetric({ key, startedAt, status: "success" });
    return result;
  } catch (error) {
    recordQueryMetric({ key, startedAt, status: "error", error });
    throw error;
  }
}

function mapInstall(row: ExecutionInstallViewRow): ExecutionInstallRow {
  const jobId = String(row.job_id ?? "");
  return {
    jobId,
    jobLabel: String(row.job_label ?? jobId),
    customerName: String(row.customer_name ?? "Unknown"),
    city: String(row.city ?? ""),
    installerName: String(row.installer_name ?? "Unassigned"),
    currentStage: String(row.current_stage ?? ""),
    scheduledInstallDate: String(row.scheduled_install_date ?? ""),
    executionAvailable: row.execution_available === true,
    unavailableReason: row.unavailable_reason ?? undefined,
    activePhaseName: row.active_phase_name ?? undefined,
    activePhaseStatus: row.active_phase_status ?? undefined,
    phases: Array.isArray(row.phases) ? row.phases : [],
    totalPhotosUploaded: int(row.total_photos_uploaded),
    totalPhotosRequired: int(row.total_photos_required),
    totalChecklistChecked: int(row.total_checklist_checked),
    totalChecklistTotal: int(row.total_checklist_total),
    lastActivityAt: row.last_activity_at ?? undefined,
    awaitingQa: row.awaiting_qa === true,
    reconciliationPending: row.reconciliation_pending === true,
    isStale: row.is_stale === true,
    qaFailuresTotal: int(row.qa_failures_total),
    lifecycle: (row.lifecycle ?? "ready") as Exclude<ExecutionLifecycle, "all">,
    lifecycleSort: int(row.lifecycle_sort),
    blockerReasons: Array.isArray(row.blocker_reasons) ? row.blocker_reasons : [],
    issueCount: int(row.issue_count),
    ticketCount: int(row.ticket_count),
    fieldFeedbackCount: int(row.field_feedback_count),
    substitutionCount: int(row.substitution_count),
    sortAt: String(row.sort_at ?? ""),
  };
}

function mapEscalation(row: ExecutionEscalationViewRow): ExecutionEscalationSignal {
  const id = String(row.id ?? "");
  return {
    id,
    kind: (row.kind ?? "stalled_cluster") as EscalationKind,
    groupKey: String(row.group_key ?? "global"),
    headline: String(row.headline ?? id),
    detail: String(row.detail ?? ""),
    severity: (row.severity ?? "low") as EscalationSeverity,
    jobIds: Array.isArray(row.job_ids) ? row.job_ids.map(String) : [],
    jobCount: int(row.job_count),
    sortRank: int(row.sort_rank),
  };
}

function mapSummary(row: ExecutionSummaryViewRow | null): ExecutionInstallSummary {
  return {
    activeCount: int(row?.active_count),
    inProgressCount: int(row?.in_progress_count),
    blockedCount: int(row?.blocked_count),
    readyCount: int(row?.ready_count),
    awaitingQaCount: int(row?.awaiting_qa_count),
    staleCount: int(row?.stale_count),
    qaFailureCount: int(row?.qa_failure_count),
    escalationCount: int(row?.escalation_count),
    highEscalationCount: int(row?.high_escalation_count),
    mediumEscalationCount: int(row?.medium_escalation_count),
    lowEscalationCount: int(row?.low_escalation_count),
  };
}

function mapEvent(row: ExecutionEventViewRow): ExecutionEventRow {
  const id = String(row.id ?? "");
  return {
    id,
    jobId: String(row.job_id ?? ""),
    jobLabel: String(row.job_label ?? row.job_id ?? ""),
    kind: (row.kind ?? "phase_submitted") as ExecutionEventKind,
    category: (row.category ?? "qa") as ExecutionEventCategory,
    headline: String(row.headline ?? id),
    detail: row.detail ?? undefined,
    actor: row.actor ?? undefined,
    at: String(row.at ?? row.sort_at ?? ""),
    sortAt: String(row.sort_at ?? row.at ?? ""),
  };
}

function mapCluster(row: ExecutionClusterViewRow): ExecutionSignalClusterRow {
  const id = String(row.id ?? "");
  return {
    id,
    group: (row.cluster_group ?? "job") as SignalClusterGroup,
    kind: (row.kind ?? "job_hotspot") as SignalClusterKind,
    label: String(row.label ?? id),
    count: int(row.count),
    detail: row.detail ?? undefined,
    jobId: row.job_id ?? undefined,
    sortOrder: int(row.sort_order),
  };
}

export const ExecutionPageService = {
  async getInstallRowsPage(query: ExecutionInstallPageQuery): Promise<PageResponse<ExecutionInstallRow>> {
    const key = `execution.install-rows.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        const from = Math.max(0, query.page * query.pageSize);
        const to = from + Math.max(1, query.pageSize) - 1;
        let request = supabase
          .from("v_execution_install_rows_ui")
          .select(INSTALL_COLUMNS, { count: "exact" })
          .order("lifecycle_sort", { ascending: true })
          .order("sort_at", { ascending: false })
          .order("job_id", { ascending: true })
          .range(from, to);

        if (query.lifecycle && query.lifecycle !== "all") {
          request = request.eq("lifecycle", query.lifecycle);
        }

        const search = query.search?.trim();
        if (search) {
          const pattern = sanitizedSearchPattern(search);
          request = request.or([
            `customer_name.ilike.${pattern}`,
            `job_label.ilike.${pattern}`,
            `city.ilike.${pattern}`,
            `installer_name.ilike.${pattern}`,
          ].join(","));
        }

        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as ExecutionInstallViewRow[]).map(mapInstall), count: count ?? 0, error: null };
      });
    } catch (error) {
      console.warn("ExecutionPageService.getInstallRowsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getEscalationsPage(query: ExecutionEscalationPageQuery): Promise<PageResponse<ExecutionEscalationSignal>> {
    const key = `execution.escalations.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        const from = Math.max(0, query.page * query.pageSize);
        const to = from + Math.max(1, query.pageSize) - 1;
        const { data, error, count } = await supabase
          .from("v_execution_escalation_signals_ui")
          .select(ESCALATION_COLUMNS, { count: "exact" })
          .order("sort_rank", { ascending: true })
          .order("job_count", { ascending: false })
          .order("id", { ascending: true })
          .range(from, to);
        if (error) throw error;
        return { data: ((data ?? []) as ExecutionEscalationViewRow[]).map(mapEscalation), count: count ?? 0, error: null };
      });
    } catch (error) {
      console.warn("ExecutionPageService.getEscalationsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getSummary(refreshToken?: number): Promise<{ data: ExecutionInstallSummary; error: unknown | null }> {
    const key = `execution.summary.${refreshToken ?? 0}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_execution_install_summary")
          .select(SUMMARY_COLUMNS)
          .maybeSingle();
        if (error) throw error;
        return { data: mapSummary(data as ExecutionSummaryViewRow | null), error: null };
      });
    } catch (error) {
      console.warn("ExecutionPageService.getSummary failed:", error);
      return { data: mapSummary(null), error };
    }
  },

  async getEventsPage(query: ExecutionEventsPageQuery): Promise<PageResponse<ExecutionEventRow>> {
    const key = `execution.events.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        const from = Math.max(0, query.page * query.pageSize);
        const to = from + Math.max(1, query.pageSize) - 1;
        let request = supabase
          .from("v_execution_event_stream_ui")
          .select(EVENT_COLUMNS, { count: "exact" })
          .order("sort_at", { ascending: false })
          .order("id", { ascending: true })
          .range(from, to);
        if (query.filter && query.filter !== "all") {
          request = request.eq("category", query.filter);
        }
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as ExecutionEventViewRow[]).map(mapEvent), count: count ?? 0, error: null };
      });
    } catch (error) {
      console.warn("ExecutionPageService.getEventsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getClusters(refreshToken?: number): Promise<{ data: ExecutionSignalClusterRow[]; error: unknown | null }> {
    const key = `execution.clusters.${refreshToken ?? 0}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_execution_signal_clusters_ui")
          .select(CLUSTER_COLUMNS)
          .order("sort_order", { ascending: true })
          .order("count", { ascending: false })
          .order("id", { ascending: true })
          .limit(24);
        if (error) throw error;
        return { data: ((data ?? []) as ExecutionClusterViewRow[]).map(mapCluster), error: null };
      });
    } catch (error) {
      console.warn("ExecutionPageService.getClusters failed:", error);
      return { data: [], error };
    }
  },
};
